import {
  Container,
  Button,
  Box,
  TextField,
  InputAdornment,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useState, useMemo } from "react";
import { useQuery } from "react-query";
import HeaderBreadcrumbs from "src/components/HeaderBreadcrumbs";
import Page from "src/components/Page";
import useSettings from "src/hooks/useSettings";
import { PATH_DASHBOARD } from "src/routes/paths";
import { useDebounce } from "use-debounce";
import { getInventory } from "../../api";
import WithoutPagination from "src/components/mrt-table/WithoutPagination";
import AddInventory from "./add";
import PubliserDetails from "./publiserDeatils";

const columns = [
  { accessorKey: "name", header: "Product Name", size: 150  },
  { accessorKey: "price", header: "Price", size: 80 },
  {
    accessorKey: "total_student_ordered_quantity",
    header: "Total Qty rec'd from actual order",
    size: 100,
  },
  {
    accessorKey: "student_ordered_quantity",
    header: "Qty rec'd from actual order",
    size: 100,
  },
  {
    accessorKey: "totalBalance",
    header: "Qty available with us",
    size: 100,
  },
  {
    accessorKey: "extraQty",
    header: "Extra Qty in Inventory",
    size: 100,
    Cell: ({ row }) => {
      const ordered = Number(row.original.student_ordered_quantity) || 0;
      const balance = Number(row.original.totalBalance) || 0;
      const extra = balance - ordered;
      return (
        <Typography
          variant="body2"
          sx={{
            color:
              extra < 0
                ? "error.main"
                : extra > 0
                ? "success.main"
                : "text.primary",
            fontWeight: 600,
          }}
        >
          {extra}
        </Typography>
      );
    },
  },
  {
    accessorKey: "totalPoQuantity",
    header: "Quantity ordered from publishers",
    size: 100,
  },
  {
    accessorKey: "totalReceivedQuantity",
    header: "Publisher received quantity",
    size: 100,
  },
  {
    accessorKey: "totalPendingOrderQty",
    header: "Qty yet to receive from the publisher",
    size: 100,
  },
  {
    accessorKey: "party_ordered_quantity",
    header: "Total party ordered quantity",
    size: 100,
  },
];

export default function InventoryDashboard() {
  const { themeStretch } = useSettings();
  const [selectedRow, setSelectedRow] = useState(null);
  const [open, setOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch] = useDebounce(searchTerm, 300);

  const { data, isLoading } = useQuery({
    queryKey: ["getInventory", debouncedSearch],
    queryFn: getInventory,
  });

  const groupedData = useMemo(() => {
    if (!data?.data) return [];

    return data.data.map((item) => {
      const totalPoQuantity = item.publisher_details?.reduce(
        (sum, pub) => sum + (Number(pub.po_quantity) || 0),
        0
      );
      const totalReceivedQuantity = item.publisher_details?.reduce(
        (sum, pub) => sum + (Number(pub.received_quantity) || 0),
        0
      );
      const totalPendingOrderQty = item.publisher_details?.reduce(
        (sum, pub) => sum + (Number(pub.pending_order_quantity) || 0),
        0
      );

      return {
        ...item,
        publishers: item.publisher_details || [],
        totalBalance: Number(item.balance) || 0,
        totalPoQuantity,
        totalReceivedQuantity,
        totalPendingOrderQty,
      };
    });
  }, [data]);

  return (
    <>
      {open && (
        <AddInventory
          onClose={() => {
            setOpen(false);
            setSelectedRow(null);
            setIsEditMode(false);
          }}
          row={selectedRow}
          isEditMode={isEditMode}
        />
      )}

      <Page title="General: Draft Order">
        <Container maxWidth={themeStretch ? false : "xl"} id="report">
          <HeaderBreadcrumbs
            heading="Inventory"
            links={[
              { name: "Dashboard", href: PATH_DASHBOARD.root },
              { name: "Inventory" },
              { name: "Product Inventory" },
            ]}
          />

          <Box sx={{ minWidth: 3000 }}>
            <WithoutPagination
              data={groupedData}
              loading={isLoading}
              columns={columns}
              enableRowActions={false}
              enableGlobalFilter={false}
              renderTopToolbarCustomActions={() => (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    padding: "8px",
                  }}
                >
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => {
                      setOpen(true);
                      setIsEditMode(false);
                    }}
                  >
                    Add New
                  </Button>

                  <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Search here..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      width: "250px",
                      "& .MuiOutlinedInput-root": { borderRadius: "8px", marginLeft: '10px'},
                    }}
                  />
                </Box>
              )}
              renderDetailPanel={({ row }) => (
                <PubliserDetails publishers={row.original.publishers} />
              )}
            />
          </Box>
        </Container>
      </Page>
    </>
  );
}
